/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import './disco-styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link } from '@ta-interaktiv/contentbricks'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import { Waypoint } from 'react-waypoint'
import { hot } from 'react-hot-loader/root'
import {
  Jos,
  Landwehr,
  BrunnerNadine,
  Muenster,
  Brunner,
  Wille,
  Burgholzer,
  Graf,
  Bissig,
  Krebs,
  Burn,
  Brupbacher,
  Mueller,
  Konic,
  Weller
} from './illus'
import { SvgBlinker } from '../svgblinker'

const ARTICLE_ID = '921118466582'
const isMobile = window.innerWidth < 600

export class AppComponent extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      page: '0/15',
      counterHidden: true
    }
  }

  render() {
    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical fitted segment'>
          <div className='ui container'>
            <h1 className='ui image header'>
              <div className='content'>
                «Auch nach vier Monaten ist mein Kurzzeitgedächtnis stark
                beeinträchtigt»
                <div className='sub header lead'>
                  Ende Mai erzählten uns 15 Personen, wie sie Covid-19 erlebten.
                  Die Protokolle zeigten, wie hart es auch «milde» Verläufe
                  treffen kann. Sind nun alle wieder gesund? Wir haben
                  nachgefragt.
                </div>
              </div>
            </h1>
          </div>
        </header>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                <nobr>Marc Brupbacher</nobr> und <nobr>Mathias Lutz</nobr>
              </div>
              <div className='ta updated'>Aktualisiert am 28. Juli 2020</div>
            </div>
            <p>
              Alle nachfolgend beschriebenen Fälle wurden nicht im Spital
              behandelt. Bei vielen Schilderungen ist es schwer fassbar, dass
              diese Fälle gemäss WHO als milde Verläufe gelten. Die Symptomatik
              wurde als extrem vielfältig, unberechenbar und teilweise
              einschneidend beschrieben. Die Mehrheit verlor Geruchs- und
              Geschmackssinn, einige haben ihn bis heute nicht wiedererlangt.
              Auch andere Beeinträchtigungen blieben nach sechs, sieben oder
              acht Wochen noch bestehen. Sars-CoV-2 ist nicht nur ein
              Lungenvirus, sondern ein Multiorganvirus, wie folgende 15 Berichte
              unserer an Covd-19 erkrankten Leserinnen und Leser von Ende Mai
              zeigten.
            </p>
            <p>
              Ende Juli haben wir bei allen Erkrankten nochmals nachgefragt, bei
              den meisten ist der Zeitpunkt des Auftretens der ersten Symptome
              nun über vier Monate her. 67 Prozent verspüren immer noch
              mindestens ein Symptom oder eine Nachwirkung. Bei rund jeder/jedem
              Vierten (27 Prozent) sind die gesundheitlichen Probleme so
              ausgeprägt, dass sie bisher noch nicht vollständig zur Arbeit
              zurückkehren konnten. Nur 33 Prozent verspüren keine Symptome
              mehr.
            </p>
            <p className='hint'>
              Fast alle Namen wurden zwecks Anonymisierung abgeändert, das Alter
              und die Angabe des Kantons sind jedoch korrekt. Die richtigen
              Namen und Adressen sind der Redaktion bekannt. Nicht alle konnten
              wegen der Im März herrschenden BAG-Empfehlung in der Schweiz auf
              Covid-19 getestet werden, wir holten auch keine Arztzeugnisse ein.
              Wir schätzten die Glaubwürdigkeit der Schilderungen durch den
              Austausch mit den Schreibenden ein und wählten jene aus, die uns
              am vertrauenswürdigsten erschienen.
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '1/15', counterHidden: false })
              }}
            />
            <Waypoint
              scrollableAncestor={window}
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ counterHidden: true })
              }}
            />
            <div className='svg-wrapper'>
              <SvgBlinker illu={<Jos />} />
            </div>
            <p className='quote'>
              «Manchmal atme ich heute noch etwas schwerer»
            </p>
            <div className='names'>
              <nobr>Martin Jos*</nobr> (64) und Familie,
              <br />
              Kanton Graubünden
            </div>
            <p>
              <span className='mark'>«</span>Meine Tochter wurde Mitte März
              positiv auf Covid-19 getestet; wo sie sich angesteckt hat, wissen
              wir nicht. Da wir alle zusammen zuvor einige Ferientage
              verbrachten, wurde die ganze vierköpfige Familie krank. Die
              Töchter sind beide Ende 20; die eine verbrachte vier Tage im Bett,
              hatte kein Fieber, aber Schwindelgefühle. Die andere Tochter
              verspürte einzig während vier Tagen einen Druck auf der Lunge.
              Meine Frau musste drei bis vier Tage das Bett hüten und fühlte
              sich anschliessend mindestens zehn Tage lang sehr müde. Alle drei
              sind aber wieder vollständig gesund. Ich selber hatte zwei Tage
              leichte Temperatur, danach fühlte ich mich wieder besser. Leider
              verlor ich den Geruchssinn, den ich bis heute, sechs Wochen
              danach, nur in sehr geringem Masse wiedererlangt habe. Auch habe
              ich das Gefühl, dass die Lunge noch nicht völlig wiederhergestellt
              ist, manchmal atme ich etwas schwerer. Meine Frau und ich sind
              bald 65 Jahre alt, niemand in der Familie hat Vorerkrankungen.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Nach 17 Wochen seit der Erkrankung ist mein Gesundheitszustand
              gut. Ich habe in der Zwischenzeit noch meine
              Lungenfunktionsfähigkeit prüfen lassen. Dieser Test ist positiv
              verlaufen. Einzig der Geruchssinn macht mir noch zu schaffen. Ich
              rieche kaum etwas. Die restlichen Familienmitglieder sind völlig
              gesund.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '2/15' })
              }}
            />
            <div className='svg-wrapper'>
              <SvgBlinker illu={<Landwehr />} />
            </div>
            <p className='quote'>
              «Mein Lungenvolumen liegt noch bei 70 Prozent»
            </p>
            <div className='names'>
              <nobr>Dominik Landwehr</nobr> (61) und Familie,
              <br />
              Winterthur
            </div>
            <p>
              <span className='mark'>«</span>Angefangen hat es Mitte März, nur
              wenige Tage vor dem Lockdown. Ich verspürte Gliederschmerzen. Mehr
              und mehr fühlte ich mich abgeschlagen und müde. Die Tage
              vergingen, Schmerzmittel brachten Linderung. Die Müdigkeit blieb,
              dazu kam Appetitverlust. Fassungslos sass ich meiner Frau
              gegenüber, die täglich drei Mahlzeiten essen konnte. Kleine
              Spaziergänge wurden zur Qual. Ich fror wie ein Schlosshund, eine
              Bettflasche half. Mit der Zeit kamen auch Fieber und leichter
              Husten dazu. Mein 30-jähriger Sohn meldete sich plötzlich mit
              Kopfschmerzen und fehlendem Geruchssinn. Wir waren ratlos. Dann
              erkrankte auch meine Frau (61), aber zum Glück nur leicht. Beide
              sind inzwischen wieder vollständig genesen. Nach etwas mehr als
              zwei Wochen war das Gröbste vorbei, die Schmerzen waren weg, der
              Appetit kam zurück. Nur die bleierne Müdigkeit blieb. Sechs Wochen
              nach den ersten Symptomen geht es mir wieder gut. Ich kann
              wandern, radfahren, koche und esse mit Appetit und arbeite. Eine
              Kontrolle bei meiner Hausärztin hat allerdings ergeben, dass mein
              Lungenvolumen zurzeit noch bei 70 Prozent liegt, obwohl ich
              subjektiv nie Atemnot verspürte. Das sei aber bei jeder Infektion
              so, meinte die Ärztin. Ich bin Asthmatiker, habe Diabetes und
              Bluthochdruck. Wo ich mich angesteckt habe, weiss ich nicht.»
            </p>

            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Mir geht es wieder gut – ich fühle mich gesund und
              leistungsfähig, bin am Wandern und mit dem Mountainbike unterwegs.
              Eine gewisse Müdigkeit ist manchmal noch zu spüren, vor allem am
              späteren Nachmittag. Was meine reduzierte Lungenfunktion angeht,
              so werde ich im August bei einem Check-up mehr erfahren.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '3/15' })
              }}
            />
            <SvgBlinker illu={<Brunner />} />
            <p className='quote'>
              «Ich hatte keine Kraft mehr, konnte kaum mehr gehen»
            </p>
            <div className='names'>
              <nobr>Maya Brunner*</nobr> (39),
              <br />
              Kanton Bern
            </div>
            <p>
              <span className='mark'>«</span>Ich bekam plötzlich Sehstörungen,
              eine gelähmte Zunge und taube Finger. Alles Anzeichen, die ich
              sonst bei einer Migräne als Vorboten habe. Schnupfen,
              Nackenschmerzen und Schüttelfrost erinnerten mich dagegen eher an
              eine leichte Grippe, die bei mir normalerweise zwei bis drei Tage
              dauert und hohes Fieber hervorruft. Meine Gedanken: Weiterarbeiten
              und am Abend früh ins Bett. Am nächsten Tag kamen aber noch
              Durchfall, Appetitlosigkeit und extreme Müdigkeit dazu. Dies
              dauerte fünf Tage. Das Fieber stieg dann aber weiter, manchmal bis
              zu 40°. Ich liess mich auf Covid-19 testen und wusste ab dann
              Bescheid. Heftiger Husten plagte mich. Ich hatte keine Kraft mehr,
              konnte kaum mehr gehen, mir war sehr schwindlig. Der Reizhusten
              wurde immer schlimmer, er raubte mir den Schlaf. Dank
              Lagerungstechniken (Kissen im Rücken, halb aufgerichtet oder
              stehend) konnte ich das Raufhusten verbessern. Wenn der Husten zu
              heftig war, drehte ich den Wasserhahn auf und atmete durch diesen
              durch, um meine Lunge zu befeuchten. Dies verflüssigte den
              Schleim. Mein Herz schlug oft extrem schnell. Ich verlor den
              Geruchs- und Geschmackssinn. Nach 13 Tagen im Bett stand ich
              erstmals wieder auf und ging mit der Maske an die frische Luft. Es
              fiel mir schwer, ich konnte mich schlecht orientieren und die
              Distanzen kaum abschätzen. Kurz später fing ich wieder mit Sport
              an, kämpfte aber immer noch gegen enorme Müdigkeit, Kurzatmigkeit
              und schwere Beine. Nach insgesamt sechs Wochen waren meine
              Beschwerden vorbei. Sporadisch wurde mir aber noch schwindlig, und
              ich bekam dazu einen Ausschlag an den Beinen. Ich habe keine
              Vorerkrankung, ich litt früher an Asthma und Heuschnupfen.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>«Ich bin ohne Symptome und fühle mich gesünder als vorher.»</p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '4/15' })
              }}
            />
            <SvgBlinker illu={<Wille />} />
            <p className='quote'>«Die Symptome waren eigenartig»</p>
            <div className='names'>
              <nobr>Katrin Wille*</nobr> (52),
              <br />
              Kanton Zürich
            </div>
            <p>
              <span className='mark'>«</span>Ich habe keine Ahnung, wo ich mich
              angesteckt habe. Es begann mit Schmerzen auf dem Brustbein. Zwei
              Tage später fühlte ich mich schlapp, und die Schmerzen verstärkten
              sich. Ich war acht Tage lang sehr müde, schnell erschöpft und
              verspürte einen anhaltenden Druck auf der Lunge, es war aber keine
              Atemnot. Inzwischen bin ich wieder ganz gesund und völlig
              symptomfrei. Insgesamt fühlte ich mich viel weniger krank als bei
              einer schweren Grippe; die Symptome waren aber eigenartig,
              insbesondere der Druck auf der Brust.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Mein Gesundheitszustand ist gleich wie vor der Erkrankung und ich
              sowie meine Familie haben keinerlei Symptome mehr.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '5/15' })
              }}
            />
            <SvgBlinker illu={<Burgholzer />} />
            <p className='quote'>«Dann wurde es heftig: Atemnot»</p>
            <div className='names'>
              <nobr>Ursula Burgholzer*</nobr> (45),
              <br />
              Kanton Solothurn
            </div>
            <p>
              <span className='mark'>«</span>Ich wurde Mitte März krank. Die
              erste Woche hatte ich nur leichte Symptome, dann wurde es heftig:
              Atemnot, die Sauerstoffsättigung fiel. Ich schlief mit der Tasche
              neben dem Bett, damit ich jederzeit ins Spital hätte gehen können.
              Die Atemschwierigkeiten haben wirklich Angst gemacht, wäre mein
              Mann nicht Arzt, so wäre ich wohl ins Spital gegangen. Insgesamt
              hatte ich sechs Wochen lang immer wieder leichtes Fieber und war
              total schlapp. Es gab auch wieder gute Tage, dann wieder
              Rückfälle. Auch heute huste ich noch und leide unter
              Muskelschmerzen, gelegentlich kommt auch das Fieber zurück.
              Demnächst werde ich meine Lungen röntgen lassen wegen möglicher
              Spätfolgen. Nun hoffe ich, wenigstens immun zu sein. Das möchte
              ich wirklich nicht nochmals erleben müssen, es war beängstigend
              für alle.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Ich arbeite inzwischen wieder 50 Prozent im Homeoffice, huste
              auch nach Monaten immer noch täglich, habe zwischendurch immer
              wieder mal abends erhöhte Temperatur, bin inzwischen beim
              Pneumologen in Behandlung. Ausserdem habe ich nun angeblich
              auffällige Herzgeräusche, die ich beim Kardiologen abklären lassen
              muss. Ich bin inzwischen ziemlich genervt über den Verlauf der
              Krankheit.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '6/15' })
              }}
            />
            <SvgBlinker illu={<Graf />} />
            <p className='quote'>
              «Körperlich stiess meine Frau an ihre Grenzen»
            </p>
            <div className='names'>
              <nobr>P. Graf</nobr> (54) und Familie
              <br />
              Kanton Appenzell I.Rh
            </div>
            <p>
              <span className='mark'>«</span>Am 16. März klagte mein 17-jähriger
              Sohn über starke Kopf- und Gliederschmerzen sowie kalte Füsse und
              Hände. Nach vier bis fünf Tagen war er wieder gesund. Eine gute
              Woche später wurde ich (54) von Tag zu Tag müder. Dazu kamen Kopf-
              und Gliederschmerzen. Diese Symptome waren viel stärker als bei
              einer Grippe. Ein Besuch beim Hausarzt zeigte, dass ich am Anfang
              einer Lungenentzündung stand. Mit Medikamenten und einem
              Entzündungshemmer in der Tasche ging ich wieder nach Hause. Am 2.
              April erhielt ich den Bescheid «Corona-positiv». Danach klagten
              meine Frau (50) und die Tochter (15) über die gleichen Symptome.
              Bei der Tochter dauerte die Krankheit mit Kopfweh und
              Gliederschmerzen auch etwa drei Wochen, wenn auch mit mildem
              Verlauf. Meine Frau hatte zeitweilig Mühe, sich richtig
              auszudrücken, sie verwechselte Wörter. Körperlich stiess sie an
              ihre Grenzen: Wie angeworfen pumpte das Herz sehr stark, sie war
              kurzatmig, und das Engegefühl in der Brust war schwer zu ertragen.
              Die Symptome tauchten immer wellenartig auf, wurden aber von Tag
              zu Tag weniger. Bei ihr dauerte die Krankheit ziemlich genau drei
              Wochen. Ab dem 8. April stand ich jeden Morgen auf und dachte,
              heute sei es besser. Eine Stunde nach dem Morgenessen wurde ich
              aber wieder müde, legte mich hin und schlief bis zu einer Stunde.
              Das ging über eine Woche so. Immer wieder hatte ich sehr starkes
              Herzklopfen. Am Wochenende vom 18./19. April erlitt ich einen
              Rückfall mit zeitweilig starkem Herzklopfen und Kurzatmigkeit. Zum
              Glück linderten sich die Symptome rasch. Mein Arzt hob die
              Quarantäne danach auf und empfahl uns, nach draussen zu gehen und
              uns zu bewegen, um Herz und Lunge wieder in Schwung zu bringen.
              Ich bewegte mich aber so schwach wie ein alter Mann. Inzwischen
              ist der 13. Mai: Das Herzklopfen ist wieder einigermassen normal,
              die Lunge braucht aber noch ein paar Tage oder Wochen Zeit. Diese
              Geduld bringe ich gerne auf, in der Hoffnung, dass es wieder ganz
              so wird wie vorher. Zum Glück war ich vorher gesund, insbesondere
              auf Herz und Lunge. Mein ältester Sohn wurde abgesehen von drei
              Tagen Müdigkeit nicht krank. Wer das Coronavirus in unsere Familie
              brachte, wissen wir nicht.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Wir sind heute allesamt genesen und so weit fast wieder gesund.
              Ich hatte bis Ende Mai gelegentlich einen Belag auf der Lunge. Bis
              Ende Juni hatte ich zudem immer wieder kurze Phasen mit starkem
              Herzklopfen. In den drei Sommerferienwochen, die jetzt zu Ende
              gehen, stellten sich vier Monate nach Krankheitsbeginn nochmals
              die Symptome Herzklopfen und Belag auf der Lunge ein. Erst in der
              dritten Woche wurden diese Symptome etwas milder. Ich hoffe, dass
              sie bald ganz abklingen. Meine Frau und meine Kinder verspüren
              alle keine Symptome mehr.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '7/15' })
              }}
            />
            <SvgBlinker illu={<Bissig />} />
            <p className='quote'>
              «Neuerdings reagiere ich sehr empfindlich auf Kälte»
            </p>
            <div className='names'>
              <nobr>Claudio Bissig*</nobr> (59),
              <br />
              Kanton Zug
            </div>
            <p>
              <span className='mark'>«</span>Anfangs März erwischte mich die
              Seuche. Die Symptome waren leichter Schnupfen,
              Bindehautentzündung, Brennen in der Lunge, starke Schleimbildung
              mit Auswurf und trockener Husten, vor allem nachts. Ich hatte kein
              Fieber. Was mich am meisten belastete, war der komplette Ausfall
              von Geruchs- und Geschmackssinn. Auf Anraten meines Arztes blieb
              ich zehn Tage zu Hause, getestet wurde ich nicht, da ich nicht zu
              den Risikogruppen gehörte. Alle Symptome bildeten sich in dieser
              Zeit zurück, bis auf meinen Geruchssinn, der ist bis heute noch
              nicht vollständig da. Neuerdings reagiere ich sehr empfindlich auf
              Kälte. Ich bin 59 Jahre alt, sportlich und habe keine bekannte
              Vorerkrankung.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Mein Covid-19 mit mildem Verlauf ist nun schon 19 Wochen her.
              Meinen allgemeinen Gesundheitszustand möchte ich als gut
              bezeichnen. Obwohl mein Geruchssinn noch nicht auf dem gleichen
              Niveau ist wie vor der Pandemie. Das könnte daran liegen, dass die
              Nasenschleimhäute immer noch eher trocken sind.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '8/15' })
              }}
            />
            <SvgBlinker illu={<Krebs />} />
            <p className='quote'>«Ich bin zur Toilette gekrochen»</p>
            <div className='names'>
              <nobr>Christina Krebs</nobr> (61),
              <br />
              Kanton Aargau
            </div>
            <p>
              <span className='mark'>«</span>Ich bin gesund, habe etwas
              Übergewicht und Rheuma in den Händen. Ich ernähre mich ausgewogen,
              rauche nicht und trinke kaum Alkohol. Mich hat es am 23. März 2020
              schlagartig erwischt: Ich bin mit Halsschmerzen, Gliederschmerzen
              und brutalem Kopfweh erwacht, und als ich aufstehen wollte, hat
              sich alles gedreht. Ich war während der ersten drei Wochen
              unendlich müde, bin fast nur im Bett gelegen, und es war mir
              extrem schwindlig. Das Rheuma in den Händen war kaum auszuhalten.
              Ich bin zur Toilette gekrochen und meist nur ganz still im Bett
              gelegen. Es war grässlich. Beim Arzt zeigte sich, dass alle Werte
              im Normalbereich waren. Die Diagnose Covid-19 haben dann meine
              Ärzte gestellt. Ich habe Schmerzmittel genommen und Reisetabletten
              gegen die Übelkeit. Zu Beginn verlor ich auch einige Tage den
              Geruchs- und Geschmackssinn. Der ist zum Glück wieder da, nur
              Kaffee mag ich nicht mehr trinken, er schmeckt mir gar nicht mehr.
              Nach der akuten Erkrankung litt ich an eigenartigen Bauchkrämpfen
              und Durchfall und Schweissausbrüchen. Heute noch muss ich abrupte
              Bewegungen vermeiden, weil mir sonst schwindlig wird. Manchmal
              bewege ich mich, als wenn ich betrunken wäre. Auch gegen Durchfall
              und Müdigkeit kämpfe ich noch immer. Aber es wird langsam, ganz,
              ganz langsam besser.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Auch nach 19 Wochen leide ich noch an Schwindelanfällen und
              Gleichgewichtsstörungen, die meist mit heftigem Kopfweh und mit
              starken Rheumaschmerzen einhergehen. Ich steuere darum noch immer
              nicht selber ein Auto. Es gibt aber glücklicherweise immer wieder
              Tage, die gut sind. Es wird besser, aber nur ganz langsam.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '9/15' })
              }}
            />
            <SvgBlinker illu={<Muenster />} twoPerson />
            <p className='quote'>«Ob die Kurzatmigkeit wieder weggeht?»</p>
            <div className='names'>
              Thomas und <nobr>Regula Münster*</nobr>, 48 und 47 Jahre,
              <br />
              Kanton Schaffhausen
            </div>
            <p>
              <span className='mark'>«</span>Meine Frau und ich waren am 9. März
              bei Lech eine Woche im Urlaub. Wir waren uns des Risikos durchaus
              bewusst - sind aber beide unter 50 und gehören zu keiner
              Risikogruppe. Nach den Ferien begann es bei mir mit leichtem
              Kratzen im Hals und Schluckschmerzen. Natürlich denkt man sofort
              an Covid-19, aber es hätte auch eine normale Erkältung sein
              können. Die Grippe selber verlief bei mir ruhig, ich hatte bis zu
              38,5 Grad Fieber, Husten. Am beunruhigendsten war aber der Verlust
              des Geruchs- und Geschmackssinns. So konnte ich mir einen
              Wattebausch mit Nagellackentferner unter die Nase halten und roch
              nichts. Ich bin grundsätzlich ziemlich fit, verspürte aber auch
              noch einige Wochen später eine Kurzatmigkeit und eine Art Brennen
              in der Lunge. Die akute Krankheit dauerte zwei bis drei Wochen.
              Ich hatte nie wirklich Angst, aber man macht sich schon Gedanken,
              ob die Kurzatmigkeit wieder weggeht. Etwa sechs Wochen nach den
              ersten Beschwerden wurde auch das besser. Ich war nie beim Arzt
              oder bei einem Test - für mich war aber klar, dass ich Covid-19
              hatte. Meine Frau wurde auch krank und erlebte es ganz ähnlich,
              einfach ohne die Atembeschwerden. Ich denke, die Chance ist gross,
              dass wir nun immun sind - zumindest für eine Weile. Das lässt uns
              etwas entspannter sein.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Wir haben beide keine Symptome oder Spätfolgen bemerkt. Wir haben
              in der Zwischenzeit den Antikörpertest gemacht und bestätigt
              bekommen, dass wir an Covid-19 erkrankt waren.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '10/15' })
              }}
            />
            <SvgBlinker illu={<Burn />} />
            <p className='quote'>
              «Wie froh ich bin, dass ich überhaupt am Leben bin»
            </p>
            <div className='names'>
              <nobr>Kristin Burn*</nobr> (54),
              <br />
              Kanton Bern
            </div>
            <p>
              <span className='mark'>«</span>Ich bin am 15. März krank geworden,
              zuerst dachte ich an eine Erkältung. Nach drei Tagen ging es schon
              besser. Danach verschlechterte sich der Zustand aber abrupt. Ich
              war so müde wie noch nie in meinem Leben. Dann verlor ich den
              Geruchs- und Geschmackssinn, litt an Atemnot, Schwindel,
              Kopfschmerzen und Herzrasen. Schliesslich wurde ich positiv auf
              Covid-19 getestet. Ins Spital ging ich nicht, im Nachhinein denke
              ich aber, dass ich ziemlich sicher Sauerstoff benötigt hätte. Nach
              einer Woche ging es mir besser, aber ich konnte immer noch nicht
              die Treppe hochsteigen, ohne mich danach sofort hinsetzen zu
              müssen. Heute fühle ich mich nun wieder gesund. Dennoch: Sobald
              ich mich etwas anstrenge, beginnt mein Puls zu rasen. Freunde, die
              einst in ihrem Leben eine Lungenentzündung durchgemacht haben,
              erzählen, dass die Regeneration sehr lange dauert. Das gibt mir
              Hoffnung, dass auch ich mich 100% von Covid-19 erholen kann. Wenn
              ich manchmal frustriert bin, denke ich, wie froh ich bin, dass ich
              überhaupt am Leben bin. Ich habe keine Vorerkrankungen.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Es ist nun 19 Wochen her seit ich an Covid-19 erkrankt bin. Ich
              bin fast zu 100% genesen, habe nur noch ab und zu bei Anstrengung
              Schmerzen im Herz. Trotzdem fürchte ich mich gelegentlich davor,
              bleibende Lungen- oder Herzschäden zurückbehalten zu haben, die
              sich dann später irgendwann mal bemerkbar machen.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '11/15' })
              }}
            />
            <SvgBlinker illu={<Brupbacher />} />
            <p className='quote'>
              «Ich konnte keinen Satz mehr sprechen, jedes Wort fehlte»
            </p>
            <div className='names'>
              <nobr>Michelle Brupbacher</nobr> (39),
              <br />
              Kanton Waadt
            </div>
            <p>
              <span className='mark'>«</span>Am 31. März fingen bei mir die
              Symptome an: Halskratzen, Husten, Fiebergefühle. Nach einigen
              Tagen fing es dann richtig an. Ich hatte zwar keinen Husten mehr,
              und mein Hals war wieder in Ordnung, aber mein Kopf fing an zu
              rasen, eine unerträgliche Qual. Ich konnte mich nicht mehr
              bewegen. Ich war hilflos mit einem kleinen Kind alleine zu Hause.
              Ich konnte unmöglich ins Spital. Nach zwei Tagen wurde es besser,
              aber ich hatte danach Probleme mit der Sprache. Ich konnte keinen
              Satz mehr sprechen, jedes Wort fehlte. Nach drei Wochen ist nun
              aber auch das besser. Dazu kam extreme Müdigkeit, sie löste bei
              mir depressive Verstimmungen aus, ich ertrage keinen Lärm mehr,
              reagiere sehr empfindlich und impulsiv. Auch mein Gedächtnis macht
              Schwierigkeiten. Ich vergesse alles, beim Kochen verbrennt mir das
              Essen, ich kaufe die falschen Sachen im Laden ein, einmal bin ich
              sogar in Pyjamahosen zum Arzt gefahren. Ich kann mich auf nichts
              konzentrieren, mir ist ständig schwindlig, und ich habe immer
              wieder das Gefühl, in Ohnmacht zu fallen. Das macht Angst. Auch
              der Geruchssinn kommt und geht.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Ich bin immer noch zu 60 Prozent arbeitsunfähig. Mich plagt nach
              wie vor eine grosse Müdigkeit. Zudem bin ich verwirrt und sehr
              unruhig, gestresst und kaum belastbar. Auch nach vier Monaten ist
              mein Kurzzeitgedächtnis noch stark beeinträchtigt.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '12/15' })
              }}
            />
            <SvgBlinker illu={<Mueller />} twoPerson />
            <p className='quote'>«Bianca hatte nachts häufig Atemnot»</p>
            <div className='names'>
              <nobr>Ralph Müller*</nobr> und <nobr>Bianca Barbon*</nobr>, beide
              39,
              <br />
              Kanton Basel-Landschaft
            </div>
            <p>
              <span className='mark'>«</span>Anfang März haben sich meine
              Freundin und ich, beide bei normaler Gesundheit, gleichzeitig bei
              einem Familienmitglied mit dem Coronavirus angesteckt. Zu Beginn
              hatten wir nur leichte Kopfschmerzen. Bianca verlor dann aber für
              einige Tage den Geruchssinn. Eine Woche später wurden die Symptome
              bei uns stärker: Halsschmerzen, Halskratzen, verstopfte Nase, und
              die Lunge begann zu schmerzen. Danach folgten mehrere Tage mit
              äusserst unangenehmem Brennen in der Brust; Bianca hatte nachts
              zusätzlich Herzrasen und häufig Atemnot. Nach vier bis fünf Tagen
              wurde es langsam besser, und wir glaubten, über den Berg zu sein.
              Die Symptome kamen aber bei uns beiden immer wieder wellenartig
              zurück. Ich litt mehrfach unter extremen Kopfschmerzen. Meine
              Freundin hat auch nach so langer Zeit immer noch Beschwerden wie
              gelegentliches Herzstolpern. Der Ruhepuls nach leichten
              Anstrengungen ging anfangs nicht unter 100 Herzschläge pro Minute.
              Nach sechs Wochen beruhigte sich das aber. Nun ist es über zwei
              Monate her seit dem Beginn der Krankheit, und wir fühlen uns immer
              noch sehr erschöpft, wir können nur in reduzierten Pensen
              arbeiten. Körperliche Arbeit wäre nicht möglich. Es fühlt sich so
              an, als ob die Entzündung immer noch aktiv wäre und die Genesung
              noch eine Weile dauern könnte. Wir machen uns Sorgen um mögliche
              langfristige Folgen für unsere Gesundheit.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Es geht weiterhin langsam aufwärts – jedoch fühlen wir uns beide
              auch nach vier Monaten weder gesund noch sind wir symptomfrei. Bei
              mir verbleiben ein leichtes Brennen im ganzen Körper, mangelnde
              körperliche Belastbarkeit, Konzentrationsstörungen und
              regelmässige akute Phasen, die weiterhin Bettruhe erfordern.
              Dazwischen gibt es auch viele gute Tage. Ich arbeite immer noch im
              reduzierten Pensum. Meine Freundin leidet stärker. Sie ist
              weiterhin im Dauerzustand der Erschöpfung und schafft es kaum,
              drei bis vier Stunden am Stück im Homeoffice zu arbeiten. Sie hat
              immer ab und zu tagelange Phasen schwerer Erschöpfung und deutlich
              weniger gute Tage als ich. Sie hat Schmerzen in den Armen und den
              Beinen und nachts weiterhin ein ausgeprägtes Herzrasen und
              Herzstolpern.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '13/15' })
              }}
            />
            <SvgBlinker illu={<Konic />} />
            <p className='quote'>
              «Ich empfand die Infektion als starke Erkältung»
            </p>
            <div className='names'>
              <nobr>Samuel Konic*</nobr> (41),
              <br />
              Kanton Basel-Landschaft
            </div>
            <p>
              <span className='mark'>«</span>Mitte März, fünf Tage, nachdem ich
              dauerhaft ins Homeoffice gewechselt bin, zeigte ich die ersten
              Symptome. Ich hatte drei Tage lang Fieber bis 38,5 Grad. Da ich
              keinen Husten hatte, hielt ich es für eine normale Erkältung. Am
              dritten Tag verschwand aber mein Geruchssinn. Ich konnte wirklich
              gar nichts mehr riechen und schmecken. Zwei Wochen danach kam er
              wieder zurück, so wie vorher ist es aber bis heute nicht. Ich war
              während der Krankheit extrem müde. Teilweise schlief ich mehrere
              Stunden am Tag. Wenn ich tief einatmete, spürte ich ein Ziehen im
              oberen Brustbereich. Drei bis vier Wochen nach Krankheitsausbruch
              war ich wieder fast vollständig hergestellt - bis auf den
              Geruchssinn. Ich empfand die Infektion als starke Erkältung, nicht
              dramatisch, aber natürlich auch alles andere als schön. Wo ich
              mich angesteckt habe, weiss ich nicht. Meine Frau und die Kinder
              (6 und 7 Jahre) sind alle symptomfrei geblieben. Strenge
              Abstandsregeln haben wir untereinander nicht eingehalten.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Ich bin fast vollständig genesen und habe keine Einschränkungen
              mehr. Ab und zu habe ich auch nach vier Monaten im Abstand von
              einigen Tagen noch Hustenanfälle. Das ist ein bisschen nervig,
              aber nicht wirklich störend.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '14/15' })
              }}
            />
            <div className='svg-wrapper'>
              <SvgBlinker illu={<BrunnerNadine />} />
            </div>
            <p className='quote'>«Am Abend bin ich heute noch erledigt»</p>
            <div className='names'>
              <nobr>Nadine Brunner*</nobr> (56),
              <br />
              Kanton Basel-Landschaft
            </div>
            <p>
              <span className='mark'>«</span>Ich hatte zwei Tage lang 39 Grad
              Fieber, musste husten und lag im Bett. Danach ging das Fieber
              zurück; ich fühlte mich etwa sieben Tage völlig energielos und
              schlapp, konnte fast nur im Bett liegen. Appetit hatte ich kaum.
              Mein Geruchs- und Geschmackssinn verabschiedete sich für drei
              Wochen komplett. Phasenweise litt ich an Durchfall und
              Kopfschmerzen. Insgesamt war ich drei Wochen krankgeschrieben.
              Aber auch vier Wochen nach Krankheitsausbruch war ich sehr müde
              und energielos, musste viel schlafen, auch am Tag. Ich hatte nie
              Atembeschwerden und war insgesamt wohl ein «leichter» Fall. Seit
              Mitte Mai arbeite ich wieder und fühle mich wohl, am Abend bin ich
              aber jeweils erledigt. Ich habe keine Vorerkrankungen und weiss
              nicht, wo ich mich angesteckt habe.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Mir geht es gut, ich fühle mich gesund und habe keine Symptome
              mehr.»
            </p>
          </div>

          <div className='ui text protocol container'>
            <Waypoint
              scrollableAncestor={window}
              bottomOffset='400px'
              onEnter={({ previousPosition, currentPosition, event }) => {
                this.setState({ page: '15/15', counterHidden: false })
              }}
            />
            <SvgBlinker illu={<Weller />} />
            <p className='quote'>
              «Ab der vierten Woche ging es langsam aufwärts»
            </p>
            <div className='names'>
              <nobr>David Weller*</nobr> (53),
              <br />
              Kanton Zürich
            </div>
            <p>
              <span className='mark'>«</span>Mitte März fieberte meine Frau
              plötzlich. Ein paar Tage später hatte auch ich leichtes Fieber,
              heftiger Husten kam dazu. Als Angehöriger einer Risikogruppe wurde
              ich getestet, und es wurde eine Erkrankung mit Covid-19
              festgestellt. Ich leide an einer Autoimmunkrankheit, die mit
              Immunsuppressiva in niedriger Dosierung behandelt wird. In der
              ersten Woche war ich mit leichtem Fieber kaum eingeschränkt und
              arbeitete im Homeoffice. In der zweiten Woche - inzwischen war
              meine Frau schon wieder symptomfrei - stieg dann das Fieber auf 39
              Grad. Ich fühlte mich sehr müde und erschöpft und verbrachte die
              meiste Zeit liegend. Zusätzlich traten Brustschmerzen auf. In der
              dritten Woche war ich wieder fieberfrei, hatte aber immer noch
              Husten und war weiterhin sehr müde. Ab der vierten Woche ging es
              langsam aufwärts, ich konnte auch wieder stundenweise arbeiten, ab
              der sechsten Woche wieder mehr oder weniger voll. Inzwischen fühle
              ich mich wieder gesund und bin dankbar, dass ich die Infektion
              trotz zusätzlichem Risiko ohne Komplikationen und Spitalaufenthalt
              überstanden habe. Unsere volljährigen Kinder blieben übrigens auch
              ohne Isolation innerhalb des Haushalts ohne Symptome.»
            </p>
            <div className='hr-theme-slash-2'>
              <div className='hr-line' />
              <div className='hr-icon'>
                <i className='icon clock outline' />
              </div>
              <div className='hr-line' />
            </div>
            <h5>Nachgefragt Ende Juli 2020</h5>
            <p>
              «Mir geht es jetzt spürbar besser als vor der Covid-19-Infektion.
              Das liegt einerseits sicher daran, dass ich mich seit der Genesung
              bewusster und häufiger bewege. Andererseits hat sich Covid-19
              irgendwie positiv auf mein Immunsystem ausgewirkt. Bei der letzten
              Untersuchung waren die ‹falschen› Antikörper meiner
              Autoimmunkrankheit auf einem rekordtiefen Stand, was sich in mehr
              Energie ausdrückt.»
            </p>
          </div>

          <Waypoint
            scrollableAncestor={window}
            onEnter={({ previousPosition, currentPosition, event }) => {
              this.setState({ counterHidden: true })
            }}
          />

          <div
            className='page-count'
            style={{ opacity: this.state.counterHidden ? '0' : '0.85' }}
          >
            <i className='clone outline icon' /> {this.state.page}
          </div>
        </div>

        <Footer
          articleId={ARTICLE_ID}
          questions={[
            'Wie informativ war der Beitrag?',
            'Wie gefällt Ihnen die Gestaltung?'
          ]}
          credits={[['Konzept und Umsetzung', 'Marc Brupbacher, Mathias Lutz']]}
        >
          <div
            className='ui container'
            style={{ margin: isMobile ? '1.2em 0 3em' : '0 0 3em' }}
          >
            <h4 className='teaser'>Weitere Beiträge zum Thema</h4>
            <ArticleTeasers
              numberOfCards='three'
              articleIds={[
                '18727947',
                '835706479183',
                '147144460867',
                '100288781781',
                '533675517866'
              ]}
            />
          </div>
        </Footer>
      </div>
    )
  }
}

export default hot('AppComponent')
